import { Badge, Button, Switch } from "@devhop/ui";
import { Edit, Folder, Plus, Trash2 } from "lucide-react";
import type { CategoriesListResult } from "@/modules/form-center/domain/types/categories";

type CategoryCard = CategoriesListResult["data"][number];

type CategoriesGridProps = {
  data: CategoryCard[];
  isLoading?: boolean;
  canDelete?: boolean;
  onEdit: (category: CategoryCard) => void;
  onToggleActive: (id: string, isActive: boolean) => void;
  onDelete?: (category: CategoryCard) => void | Promise<void>;
  onCreate: () => void;
};

const getCategoryIcon = (_icon: string | null | undefined) => {
  // Map icon strings to actual icons - for now using Folder as default
  return <Folder className="h-6 w-6" />;
};

export function CategoriesGrid({
  data,
  isLoading,
  canDelete,
  onEdit,
  onToggleActive,
  onDelete,
  onCreate,
}: CategoriesGridProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => {
          const key = `skeleton-${Date.now()}-${i}`;
          return (
            <div
              key={key}
              className="h-48 animate-pulse rounded-lg border bg-muted"
            />
          );
        })}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {data.map((category) => (
        <div
          key={category.id}
          className="group relative rounded-lg border bg-card p-4 transition-shadow hover:shadow-md"
        >
          <div className="mb-3 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                {getCategoryIcon(category.icon)}
              </div>
              <div>
                <h3 className="font-semibold">{category.name}</h3>
                <p className="text-muted-foreground text-xs">
                  ID: {category.id.slice(0, 8).toUpperCase()}
                </p>
              </div>
            </div>
            <div className="flex opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(category)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              {canDelete && onDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => void onDelete(category)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {category.description && (
            <p className="mb-3 line-clamp-2 text-muted-foreground text-sm">
              {category.description}
            </p>
          )}

          <div className="mb-3 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">ຟອມທີ່ໃຊ້ງານ:</span>
              <Badge variant="secondary">
                {category.activeFormsCount ?? 0}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">ລໍາດັບ:</span>
              <Badge variant="outline">{category.orderIndex}</Badge>
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-3">
            <div className="flex items-center gap-2">
              <Switch
                checked={category.isActive}
                onCheckedChange={(checked) =>
                  onToggleActive(category.id, checked)
                }
              />
              <span className="text-xs">
                {category.isActive ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
              </span>
            </div>
          </div>
        </div>
      ))}

      {/* Add New Category Card */}
      <button
        type="button"
        onClick={onCreate}
        className="group flex min-h-[192px] flex-col items-center justify-center rounded-lg border-2 border-muted-foreground/25 border-dashed bg-card p-4 transition-colors hover:border-primary hover:bg-primary/5"
      >
        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Plus className="h-5 w-5 text-muted-foreground group-hover:text-primary" />
        </div>
        <span className="font-medium text-muted-foreground text-sm group-hover:text-primary">
          ເພີ່ມຫມວດຫມູ່ໃໝ່
        </span>
      </button>
    </div>
  );
}
